<template>
    <div class="planner">

        <!-- BACKLOG (scroll indépendant) -->
        <div class="planner-backlog" ref="backlog">
            <div
                v-for="(event, index) in unplannedEvents"
                :key="'unplanned-' + index"
                class="backlog-card"
                :data-backlog-index="index"
            >
                <div class="backlog-title">
                    INT #{{ event.intervention_id }}
                </div>
            </div>
        </div>

        <!-- ZONE SCROLLABLE DU PLANNING -->
        <div class="planner-scroll" ref="scrollContainer">

            <!-- HEADER (fait partie du scroll planning) -->
            <div class="planner-header">
                <div class="tech-col"></div>

                <div
                    v-for="slot in timeSlots"
                    :key="slot"
                    class="time-col"
                >
                    {{ slot }}
                </div>
            </div>

            <!-- BODY -->
            <div class="planner-body" ref="grid">

                <!-- LIGNES TECHNICIENS -->
                <div
                    v-for="tech in techniciens"
                    :key="tech.id"
                    class="tech-row"
                    :style="{ height: rowHeight + 'px' }"
                >
                    <div
                        class="tech-avatar"
                        :style="tech.avatar
                            ? { backgroundImage: `url(${tech.avatar})` }
                            : { backgroundColor: '#cbd5e1' }"
                    >
                        <span
                            v-if="!tech.avatar"
                            class="tech-initials"
                        >
                            {{ tech.prenom.charAt(0) }}{{ tech.nom.charAt(0) }}
                        </span>
                    </div>

                    <div class="tech-label">
                        {{ tech.prenom }} {{ tech.nom }}
                    </div>
                </div>

                <!-- NOW LINE -->
                <div
                    v-if="nowPosition !== null"
                    class="now-line"
                    :style="{ left: nowPosition + 'px' }"
                ></div>

                <!-- EVENTS -->
                <div
                    v-for="(event, index) in computedEvents"
                    :key="index"
                    class="event"
                    :class="event.type"
                    :style="event.style"
                    :data-index="index"
                >
                    <div class="event-time">
                        {{ formatTime(event.heure_debut) }} - {{ formatTime(event.heure_fin) }}
                    </div>

                    <div class="event-title">
                        <template v-if="event.type === 'intervention'">
                            INT #{{ event.intervention_id }}
                        </template>
                        <template v-else>
                            Trajet
                        </template>
                    </div>
                </div>

            </div>
        </div>

    </div>
</template>

<script>
import dayjs from "dayjs"
import interact from "interactjs"

export default {

    name: "Planner",

    props: {
        events: { type: Array, required: true },
        unplannedEvents: { type: Array, default: () => [] },
        techniciens: { type: Array, required: true },
        startHour: { type: Number, default: 7 },
        endHour: { type: Number, default: 18 },
        planningDate: {
            type: String, // format "YYYY-MM-DD"
            required: true
        },
        step: { type: Number, default: 10 }, // Affichage
        moveStep: { type: Number, default: 1 }   // déplacement
    },

    data() {
        return {
            colWidth: 60,
            rowHeight: 80,
            labelWidth: 140,
            internalEvents: [],
            now: dayjs()
        }
    },

    computed: {

        timeSlots() {
            const slots = []
            let current = dayjs().hour(this.startHour).minute(0)

            while (current.hour() < this.endHour) {
                slots.push(current.format("HH:mm"))
                current = current.add(this.step, "minute")
            }

            return slots
        },

        computedEvents() {

            // ===============================
            // Heure actuelle en minutes
            // ===============================
            const nowMinutes =
                this.now.hour() * 60 + this.now.minute()

            const today = dayjs().format("YYYY-MM-DD")
            const isToday = this.planningDate === today
            const isPastDay = this.planningDate < today
            const isFutureDay = this.planningDate > today

            return this.internalEvents.map(event => {

                // ===============================
                // Conversion heures event → minutes
                // ===============================
                const startMin = this.timeToMinutes(event.heure_debut)
                const endMin   = this.timeToMinutes(event.heure_fin)

                // ===============================
                // Position verticale (technicien)
                // ===============================
                const techIndex = this.techniciens.findIndex(
                    t => t.id === event.technicien_id
                )

                const top = techIndex * this.rowHeight

                // ===============================
                // Position horizontale (temps)
                // ===============================
                const left =
                    ((startMin - this.startHour * 60) / this.step) *
                    this.colWidth +
                    this.labelWidth

                const width =
                    ((endMin - startMin) / this.step) *
                    this.colWidth

                // ===============================
                // OPACITY LOGIC
                // ===============================

                let opacity = 1

                if (event.isGhost) {

                    // Ghost prioritaire
                    opacity = 0.6

                } else if (isFutureDay) {

                    // Toute la journée est future
                    opacity = 0.8

                } else if (isPastDay) {

                    // Toute la journée est passée
                    opacity = 1

                } else if (isToday) {

                    // Event complètement futur
                    if (startMin > nowMinutes) {
                        opacity = 0.8
                    }

                    // Event en cours ou passé
                    if (startMin <= nowMinutes) {
                        opacity = 1
                    }
                }

                // ===============================
                // PROGRESS CUT EFFECT
                // ===============================
                // On calcule la largeur "passée"
                // pour découper visuellement l'event
                // ===============================

                let progressWidth = 0

                if (isToday) {

                    // Event complètement passé
                    if (endMin <= nowMinutes) {
                        progressWidth = width
                    }

                    // Event en cours
                    else if (startMin < nowMinutes && endMin > nowMinutes) {

                        const elapsed = nowMinutes - startMin
                        const total   = endMin - startMin

                        const ratio = elapsed / total
                        progressWidth = ratio * width
                    }

                    // Event futur → progressWidth reste à 0
                }

                // Journée passée → tout est complété
                if (isPastDay) {
                    progressWidth = width
                }

                // Journée future → rien n'est commencé
                if (isFutureDay) {
                    progressWidth = 0
                }

                // ===============================
                // Retour objet stylé
                // ===============================

                return {
                    ...event,
                    style: {
                        left: left + "px",
                        width: width + "px",
                        top: top + "px",
                        height: (this.rowHeight - 16) + "px",
                        opacity: opacity,
                        "--progress-width": progressWidth + "px"
                    }
                }
            })
        },

        nowPosition() {

            const today = dayjs().format("YYYY-MM-DD")

            if (this.planningDate !== today) {
                return null
            }

            const nowMinutes =
                this.now.hour() * 60 + this.now.minute()

            const start = this.startHour * 60
            const end   = this.endHour * 60

            if (nowMinutes < start || nowMinutes > end) {
                return null
            }

            const minutesFromStart = nowMinutes - start

            return (
                (minutesFromStart / this.step) *
                this.colWidth +
                this.labelWidth
            )
        }
    },

    watch: {
        events: {
            immediate: true,
            handler(val) {
                this.internalEvents = (val || []).filter(e =>
                    this.timeToMinutes(e.heure_fin) >
                    this.timeToMinutes(e.heure_debut)
                )
            }
        }
    },

    mounted() {
        this.$nextTick(() => this.initDrag())
        this.initBacklogDrag()

        setInterval(() => {
            this.now = dayjs()
        }, 60000) // update chaque minute
    },

    methods: {

        formatTime(time) {
            return time ? time.slice(0, 5) : ""
        },

        timeToMinutes(time) {
            const [h, m] = time.split(":")
            return parseInt(h) * 60 + parseInt(m)
        },

        minutesToTime(minutes) {
            const h = Math.floor(minutes / 60)
            const m = minutes % 60
            return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:00`
        },

        initDrag() {

            interact(".event").draggable({
                inertia: false,
                listeners: {

                    start: () => {
                        document.body.style.userSelect = "none"
                    },

                    move: event => {

                        const grid = this.$refs.grid
                        const container = grid.parentElement
                        const containerRect = container.getBoundingClientRect()

                        const scrollZone = 80
                        const scrollSpeed = 20

                        if (event.clientX > containerRect.right - scrollZone) {
                            container.scrollLeft += scrollSpeed
                        }

                        if (event.clientX < containerRect.left + scrollZone) {
                            container.scrollLeft -= scrollSpeed
                        }

                        const relativeX =
                            event.clientX -
                            containerRect.left -
                            this.labelWidth +
                            container.scrollLeft

                        const relativeY =
                            event.clientY -
                            containerRect.top

                        const index = event.target.dataset.index
                        const item = this.internalEvents[index]

                        if (!item) {
                            return
                        }

                        const totalMinutesFromStart = ((relativeX / this.colWidth) * this.step)

                        const snappedMinutes = Math.floor(totalMinutesFromStart / this.moveStep) * this.moveStep

                        const newStart = this.startHour * 60 + snappedMinutes

                        const minStart = this.startHour * 60
                        const maxStart = this.endHour * 60

                        // let newStart = this.startHour * 60 + minutesOffset

                        const duration =
                            this.timeToMinutes(item.heure_fin) -
                            this.timeToMinutes(item.heure_debut)

                        if (newStart < minStart) {
                            newStart = minStart
                        }

                        if (newStart + duration > maxStart) {
                            newStart = maxStart - duration
                        }

                        const newEnd = newStart + duration

                        const techIndex = Math.floor(relativeY / this.rowHeight)
                        let newTechId = item.technicien_id

                        if (techIndex >= 0 && techIndex < this.techniciens.length) {
                            newTechId = this.techniciens[techIndex].id
                        }

                        const collision = this.hasCollision(
                            item,
                            newStart,
                            newEnd,
                            newTechId
                        )

                        if (!collision) {

                            item.heure_debut = this.minutesToTime(newStart)
                            item.heure_fin   = this.minutesToTime(newEnd)
                            item.technicien_id = newTechId

                        }

                        // if (techIndex >= 0 && techIndex < this.techniciens.length) {
                        //     item.technicien_id = this.techniciens[techIndex].id
                        // }
                    },

                    end: event => {

                        document.body.style.userSelect = ""

                        const index = event.target.dataset.index
                        const item = this.internalEvents[index]

                        if (!item) {
                            return
                        }

                        this.$emit("update-event", { ...item })
                    }
                }
            })
        },

        hasCollision(movingEvent, newStart, newEnd, techId) {
            return this.internalEvents.some(e => {

                // On ignore l'event qu'on déplace
                if (e === movingEvent) return false

                // On ignore les autres techniciens
                if (e.technicien_id !== techId) return false

                const eStart = this.timeToMinutes(e.heure_debut)
                const eEnd   = this.timeToMinutes(e.heure_fin)

                // Collision stricte
                return newStart < eEnd && newEnd > eStart
            })
        },

        initBacklogDrag() {
            interact(".backlog-card").draggable({
                inertia: false,

                listeners: {

                    start: event => {

                        const backlogIndex =
                            event.target.dataset.backlogIndex

                        const backlogItem =
                            this.unplannedEvents[backlogIndex]

                        const duration =
                            parseInt(backlogItem.duree_estimee || 60)

                        const ghostEvent = {
                            ...backlogItem,
                            heure_debut: this.minutesToTime(this.startHour * 60),
                            heure_fin: this.minutesToTime(this.startHour * 60 + duration),
                            technicien_id: null,
                            isGhost: true
                        }

                        this.internalEvents.push(ghostEvent)

                        event.target._ghostRef = ghostEvent
                    },

                    move: event => {

                        const ghost = event.target._ghostRef
                        if (!ghost) return

                        const grid = this.$refs.grid
                        const container = grid.parentElement
                        const rect = container.getBoundingClientRect()

                        if (event.clientY < rect.top) return

                        const relativeX =
                            event.clientX -
                            rect.left -
                            this.labelWidth +
                            container.scrollLeft

                        const relativeY =
                            event.clientY -
                            rect.top

                        const techIndex =
                            Math.floor(relativeY / this.rowHeight)

                        if (
                            techIndex < 0 ||
                            techIndex >= this.techniciens.length
                        ) {
                            return
                        }

                        const snappedCol =
                            Math.floor(relativeX / this.colWidth)

                        const startMinutes =
                            this.startHour * 60 +
                            snappedCol * this.step

                        const duration =
                            this.timeToMinutes(ghost.heure_fin) -
                            this.timeToMinutes(ghost.heure_debut)

                        const newEnd = startMinutes + duration

                        const collision = this.hasCollision(
                            ghost,
                            startMinutes,
                            newEnd,
                            this.techniciens[techIndex].id
                        )

                        if (!collision) {

                            ghost.heure_debut =
                                this.minutesToTime(startMinutes)

                            ghost.heure_fin =
                                this.minutesToTime(newEnd)

                            ghost.technicien_id =
                                this.techniciens[techIndex].id
                        }
                    },

                    end: event => {

                        const ghost = event.target._ghostRef
                        if (!ghost) return

                        if (!ghost.technicien_id) {
                            // Drop invalide → on supprime
                            this.internalEvents =
                                this.internalEvents.filter(e => e !== ghost)
                        } else {
                            // On confirme
                            ghost.isGhost = false
                            this.$emit("create-event", ghost)
                            this.$emit("remove-unplanned", ghost.intervention_id)
                        }

                        event.target._ghostRef = null
                    }
                }
            })
        },

        handleCreate(newEvent) {
            this.events.push(newEvent)
        },

        handleRemoveUnplanned(interventionId) {
            this.unplannedEvents =
                this.unplannedEvents.filter(
                    e => e.intervention_id !== interventionId
                )
        }
    }
}
</script>

<style scoped>

/* ============================= */
/* CONTENEUR PRINCIPAL           */
/* ============================= */

.planner {
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.now-line {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #ef4444;
    z-index: 200;
    pointer-events: none;
}

/* ============================= */
/* BACKLOG                       */
/* ============================= */

.planner-backlog {
    display: flex;
    gap: 12px;
    padding: 14px;
    overflow-x: auto;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    user-select: none;
}

.backlog-card {
    min-width: 180px;
    padding: 14px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    cursor: grab;
    box-shadow: 0 4px 8px rgba(0,0,0,0.04);
    flex-shrink: 0;
    transition: all 0.15s ease;
}

.backlog-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(0,0,0,0.06);
}

.backlog-card:active {
    cursor: grabbing;
    opacity: 0.85;
}

.backlog-title {
    font-weight: 700;
    font-size: 13px;
    color: #1e293b;
}

/* ============================= */
/* ZONE SCROLLABLE DU PLANNING  */
/* ============================= */

.planner-scroll {
    overflow-x: auto;
    position: relative;
}

/* ============================= */
/* HEADER                        */
/* ============================= */

.planner-header {
    display: flex;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    min-width: max-content;
    position: sticky;
    top: 0;
    z-index: 20;
}

.tech-col {
    width: 140px;
    flex-shrink: 0;
    border-right: 1px solid #e2e8f0;
    background: #f8fafc;
}

.time-col {
    width: 60px;
    min-width: 60px;
    flex: 0 0 60px;
    text-align: center;
    font-size: 11px;
    padding: 12px 0;
    border-right: 1px solid #f1f5f9;
    font-weight: 700;
    color: #64748b;
    user-select: none;
    letter-spacing: 0.05em;
}

/* ============================= */
/* BODY                          */
/* ============================= */

.planner-body {
    position: relative;
    min-width: max-content;
}

/* Grille verticale propre */
.planner-body::before {
    content: "";
    position: absolute;
    top: 0;
    left: 140px;
    right: 0;
    bottom: 0;
    background-image: linear-gradient(to right, #f1f5f9 1px, transparent 1px);
    background-size: 60px 100%;
    pointer-events: none;
}

/* ============================= */
/* LIGNES TECHNICIENS            */
/* ============================= */

.tech-row {
    display: flex;
    border-bottom: 1px solid #f1f5f9;
    align-items: center;
    background: #ffffff;
    position: relative;
    z-index: 1;
}

.tech-row:hover {
    background: #fcfdff;
}

.tech-avatar {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    margin-left: 12px;
    background-size: cover;
    background-position: center;
    border: 1px solid #e2e8f0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.tech-initials {
    font-weight: 700;
    color: #475569;
    font-size: 14px;
}

.tech-label {
    width: 98px;
    padding: 0 12px;
    font-weight: 600;
    font-size: 12px;
    color: #1e293b;
    border-right: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    background: #ffffff;
}

/* ============================= */
/* EVENTS                        */
/* ============================= */

.event {
    position: absolute;
    overflow: hidden;
    background: #4f46e5;
    color: #ffffff;
    border-radius: 10px;
    cursor: grab;
    font-size: 12px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
    z-index: 5;
    user-select: none;
}

.event:active {
    cursor: grabbing;
    transform: scale(1.02);
    box-shadow: 0 16px 30px rgba(0,0,0,0.18);
    z-index: 100;
}

.event::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: var(--progress-width, 0px);
    background: rgba(0,0,0,0.15);
    pointer-events: none;
}

.event::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: var(--progress-width, 0px);
    background: rgba(0,0,0,0.15);
    pointer-events: none;
}

/* Partie future (droite) */
.event::after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: calc(100% - var(--progress-width, 0px));
    background: rgba(255,255,255,0.25);
    pointer-events: none;
}

/* Variante trajet */

.event.trajet {
    background: #ffffff;
    color: #475569;
    border: 1px dashed #cbd5e1;
    box-shadow: none;
}

/* Texte interne */

.event-time {
    font-size: 10px;
    font-weight: 800;
    margin-bottom: 3px;
    opacity: 0.85;
}

.event-title {
    font-weight: 700;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ============================= */
/* SCROLLBAR PREMIUM             */
/* ============================= */

.planner-backlog::-webkit-scrollbar,
.planner-scroll::-webkit-scrollbar {
    height: 10px;
}

.planner-backlog::-webkit-scrollbar-track,
.planner-scroll::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.planner-backlog::-webkit-scrollbar-thumb,
.planner-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
}

.planner-backlog::-webkit-scrollbar-thumb:hover,
.planner-scroll::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

</style>